// Stand-in backend for the console's live preview: realistic addresses, EPC
// look-ups and a plausible quote, with short delays so loading states show.
import { compactGrant, estimateRequiredKw, normalisePricing, quoteFor, type WidgetPricing } from "./pricing";
import type { Address, FullLookup, FullProperty, GrantResult, LL, PropertyDetails, PropertyLookup, Quote, SolarEstimate, SolarEstimateRequest, SolarLocateRequest, SolarLocation, SolarRoof, WidgetApi } from "./types";

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const STREETS = ["Oak Avenue", "Mill Lane", "Church Road", "Station Road", "Victoria Street", "Park View"];

// Sample EPC results, cycled by address. The last address has no EPC, so the
// preview also shows the "tell us about your home" fallback.
const SAMPLE_EPCS: (PropertyLookup | null)[] = [
  { details: { propertyType: "semi-detached", bedrooms: 3, heating: "gas" }, epcRating: "D", floorArea: 92 },
  { details: { propertyType: "detached", bedrooms: 4, heating: "oil" }, epcRating: "E", floorArea: 148 },
  { details: { propertyType: "terraced", bedrooms: 2, heating: "gas" }, epcRating: "C", floorArea: 71 },
  { details: { propertyType: "bungalow", bedrooms: 2, heating: "lpg" }, epcRating: "E", floorArea: 84 },
  { details: { propertyType: "flat", bedrooms: 1, heating: "electric" }, epcRating: "C", floorArea: 48 },
  { details: { propertyType: "semi-detached", bedrooms: 3 }, epcRating: "D", floorArea: 88 }, // EPC without a fuel
  { details: { propertyType: "terraced", bedrooms: 3, heating: "gas" }, epcRating: "D", floorArea: 95 },
  null,
];

// Full widget extras for each sample (the EPC card and Property page pre-fill)
const SAMPLE_FULL: (Partial<FullProperty> & { band: string; score: number; potential: [string, number] })[] = [
  { kind: "house", builtForm: "semi-detached", ageBand: "D", storeys: 2, fuel: "gas", wallsInsulated: true, roofInsulated: true, floorInsulated: false, mainsGas: true, band: "D", score: 61, potential: ["B", 83] },
  { kind: "house", builtForm: "detached", ageBand: "B", storeys: 2, fuel: "oil", wallsInsulated: false, roofInsulated: true, floorInsulated: false, mainsGas: false, band: "E", score: 43, potential: ["D", 61] },
  { kind: "house", builtForm: "mid-terrace", ageBand: "B", storeys: 2, fuel: "gas", wallsInsulated: false, roofInsulated: true, floorInsulated: false, mainsGas: true, band: "C", score: 70, potential: ["B", 84] },
  { kind: "bungalow", builtForm: "detached", ageBand: "E", storeys: 1, fuel: "lpg", wallsInsulated: true, roofInsulated: true, floorInsulated: false, mainsGas: false, band: "E", score: 50, potential: ["C", 72] },
  { kind: "flat", builtForm: "mid-terrace", ageBand: "H", storeys: 1, fuel: "electric", wallsInsulated: true, roofInsulated: null, floorInsulated: null, mainsGas: false, band: "C", score: 72, potential: ["C", 75] },
  { kind: "house", builtForm: "semi-detached", ageBand: "C", storeys: 2, wallsInsulated: false, roofInsulated: true, floorInsulated: false, band: "D", score: 57, potential: ["B", 82] },
  { kind: "house", builtForm: "end-terrace", ageBand: "A", storeys: 3, fuel: "gas", wallsInsulated: false, roofInsulated: false, floorInsulated: false, mainsGas: true, band: "D", score: 55, potential: ["C", 74] },
];

function sampleFull(i: number, lookup: PropertyLookup, address: Address): FullLookup {
  const f = SAMPLE_FULL[i % SAMPLE_FULL.length];
  const { band, score, potential, ...property } = f;
  const parts = address.line.split(", ");
  return {
    // Obviously sample data: the preview never shows a real certificate
    certificate: {
      number: `0000-0000-0000-0000-000${i + 1}`,
      lodgedDate: "2021-06-14",
      inspectionDate: "2021-06-10",
      addressLines: parts,
      country: "England",
      current: { score, band },
      potential: { score: potential[1], band: potential[0] },
    },
    property: { ...property, floorArea: lookup.floorArea, roomInRoof: false, heatingSystem: "boiler-radiators", hotWater: "main-system" },
    country: "England",
  };
}

/** Preview grant check: the same rules as the API (grant.py), for sample answers. */
function sampleGrant(property: FullProperty, a: { owner: boolean; newBuild: boolean; previousFunding: boolean; mainsGas: boolean | null }): GrantResult {
  const replacing = ["gas", "oil", "lpg", "electric"].includes(property.fuel);
  const checks: GrantResult["checks"] = [
    { key: "owner", text: "Own the property", status: a.owner ? "pass" : "fail", detail: a.owner ? "You own the property" : "The grant is only for property owners" },
    { key: "country", text: "In England or Wales", status: "pass", detail: "Your home is in England" },
    { key: "replacing", text: "Replacing a fossil fuel or electric heating system", status: replacing ? "pass" : "fail", detail: replacing ? `Your heating is ${property.fuel}` : "The grant is for replacing fossil fuel or electric heating" },
    { key: "existing", text: "Not a new build", status: a.newBuild ? "fail" : "pass", detail: a.newBuild ? "Most new builds aren't eligible" : "An existing home" },
    { key: "funding", text: "No previous public funding for a heat pump here", status: a.previousFunding ? "fail" : "pass", detail: a.previousFunding ? "Usually not eligible" : "No previous heat pump funding at this address" },
    { key: "epc", text: "Energy Performance Certificate", status: "info", detail: "Certificate 0000-0000-0000-0000-0001 (sample)" },
  ];
  const eligible = checks.every((c) => c.status !== "fail");
  const uplift = eligible && (property.fuel === "oil" || property.fuel === "lpg") && a.mainsGas === false;
  if (eligible && (property.fuel === "oil" || property.fuel === "lpg"))
    checks.push({ key: "uplift", text: "Off-gas grid oil/LPG uplift", status: uplift ? "pass" : "info", detail: uplift ? "An extra £1,500, £9,000 in total (until 31 March 2027)" : "Only for homes with no mains gas connection" });
  return { grant: eligible ? (uplift ? 9000 : 7500) : 0, uplift, eligible, checks, note: "A guide only: your installer confirms eligibility." };
}

/**
 * Where a solar preview gets its roofs and figures. The roof engine and the price model
 * run only on the server (never in the public widget), so the console passes a backend
 * that calls its own preview route with the draft prices.
 */
export interface SolarBackend {
  locate(req: SolarLocateRequest): Promise<SolarLocation>;
  building(at: LL): Promise<SolarRoof>;
  estimate(req: SolarEstimateRequest): Promise<SolarEstimate>;
}

const noSolar = () => Promise.reject(new Error("The roof scan preview isn't available here."));

/** Mock backend quoting with the installer's pricing (sample prices if none). Pass a
 *  getter to always quote with the latest prices (the preview does). */
export function createMockApi(
  pricing: WidgetPricing | (() => WidgetPricing) = normalisePricing(),
  opts: { solar?: SolarBackend } = {},
): WidgetApi {
  const solar = opts.solar;
  return {
  async searchAddresses(postcode) {
    await wait(350);
    const pc = postcode.toUpperCase().replace(/\s+/g, " ").trim();
    const street = STREETS[pc.length % STREETS.length];
    return Array.from({ length: SAMPLE_EPCS.length }, (_, i): Address => ({
      id: `mock-${i + 1}`,
      line: `${(i + 1) * 2} ${street}, Anytown, ${pc}`,
    }));
  },

  async lookupProperty(address: Address) {
    await wait(400);
    const i = Number(address.id.replace("mock-", "")) - 1;
    const lookup = SAMPLE_EPCS[i % SAMPLE_EPCS.length];
    return lookup ? { ...lookup, full: sampleFull(i, lookup, address) } : null;
  },

  async getQuote(_address: Address, d: PropertyDetails, full): Promise<Quote> {
    await wait(450);
    const p = typeof pricing === "function" ? pricing() : pricing;
    if (!full) {
      // Same sizing + pricing rules the real API uses, with the installer's prices
      return quoteFor(p, estimateRequiredKw(d), compactGrant(d.heating));
    }
    // Full preview: sample figures shaped like the API's heat loss breakdown
    // (the real calculation runs on the API with the RdSAP 10 tables)
    const area = full.property.floorArea || 90;
    const elements = [
      { element: "External walls", area: Math.round(area * 1.1), u: full.property.wallsInsulated ? 0.55 : 1.5 },
      { element: "Roof", area: Math.round(area / full.property.storeys), u: full.property.roofInsulated ? 0.25 : 2.3 },
      { element: "Ground floor", area: Math.round(area / full.property.storeys), u: full.property.floorInsulated ? 0.3 : 0.7 },
      { element: "Windows", area: Math.round(area * 0.15), u: 2.8 },
      { element: "Doors", area: 3.7, u: 3.0 },
    ].map((e) => ({ ...e, wPerK: Math.round(e.area * e.u) }));
    const ventilation = { element: "Ventilation (sample)", wPerK: Math.round(0.33 * 0.5 * area * 2.5) };
    const total = elements.reduce((n, e) => n + e.wPerK, 0) + ventilation.wPerK;
    const kw = Math.round((total * 24.5) / 100) / 10; // W/K × 24.5 K → kW, 1 decimal
    // The grant the homeowner chose on the grant step (0 = skipped or not eligible)
    const grant = full.includeGrant ? sampleGrant(full.property, full.grantAnswers).grant : 0;
    const q = quoteFor(p, kw, grant);
    return {
      ...q,
      heatLoss: {
        kw, level: "A", internalC: 21, externalC: -3.5, designLocation: "Sample location",
        elements: [...elements.map((e) => ({ ...e, source: "sample" })), ventilation], totalWPerK: total,
        method: "Sample figures for the preview",
      },
    };
  },

  async checkGrant(_address, property, answers) {
    await wait(300);
    return sampleGrant(property, answers);
  },

  async submitLead() {
    await wait(400);
  },

  // Contact form preview: nothing is sent
  async submitContact() {
    await wait(400);
  },

  // ── Solar (docs/12 §10): from the backend passed in (no backend: the "couldn't scan" step) ──
  solarLocate: (req) => (solar ? solar.locate(req) : noSolar()),
  solarBuilding: (at) => (solar ? solar.building(at) : noSolar()),
  solarEstimate: (req) => (solar ? solar.estimate(req) : noSolar()),
  async submitSolarLead() {
    await wait(400);
  },
  };
}

/** Mock backend with sample prices. */
export const mockApi: WidgetApi = createMockApi();
